import { useEffect, useMemo, useState } from 'react'
// DEBUG: useNavigate import removed as it's not used

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'
import { Company } from '@/interfaces/company'
import { Modal } from '@/components/new'
import NewCompanyModal from '../components/NewCompanyModal'
import Button from '@/components/misc/Button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { GET_ALL_COMPANIES_QUERY_KEY } from '../hooks/useServerLogisticCompany'
import { deleteCompany } from '@/api/companyAPI'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  showEditCompany: boolean
  onClose: () => void
  companyToEdit: Company | undefined
  showDeleteCompany: boolean
  onCloseDelete: () => void
  companyToDelete: number | undefined
}

const EXPEDIENT_TABLE_COLUMNS_STORAGE_KEY = 'expedient-table-columns'

export function DataTable<TData, TValue>({
  columns,
  showEditCompany,
  onClose,
  companyToEdit,
  showDeleteCompany,
  onCloseDelete,
  companyToDelete,
}: DataTableProps<TData, TValue>) {
  // DEBUG: Navigate function available but not currently used

  // Use the table context
  const {
    currentPage,
    companyData,
    isLoading,
    skeletonRowCount,
    isFetching,
    isError,
  } = useTableContext()

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [isMobileView, setIsMobileView] = useState(false)
  const queryClient = useQueryClient()
  const [isDeleting, setIsDeleting] = useState(false)

  // Create skeleton columns when loading
  const skeletonColumns = useMemo(() => {
    if (!isLoading) return columns

    return columns.map((column) => {
      // Different width based on column type
      const width = '80%'
      const height = '20px'

      return {
        ...column,
        cell: () => (
          <div className="flex items-center">
            <Skeleton className={`h-${height} w-${width}`} />
          </div>
        ),
      }
    })
  }, [columns, isLoading])

  // Create skeleton data for loading state
  const skeletonData = useMemo(() => {
    if (!isLoading) return []

    return Array.from({ length: skeletonRowCount }).map((_, index) => ({
      id: `skeleton-${index}`,
    }))
  }, [isLoading, skeletonRowCount])

  // Create a memoized version of the data to ensure stable references
  const tableData = useMemo(() => {
    if (isLoading) {
      return skeletonData as TData[]
    }
    return (companyData?.items as TData[]) || []
  }, [companyData?.items, isLoading, skeletonData])

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // Set default column visibility based on screen size
  useEffect(() => {
    if (isMobileView) {
      // On mobile, show only high-priority columns
      const mobileVisibility: VisibilityState = {
        origin: false,
        shipping: false,
        totalItems: false,
        areaSales: false,
        deliveryAt: false,
        paidAt: false,
      }
      setColumnVisibility((prev) => ({ ...prev, ...mobileVisibility }))
    }
  }, [isMobileView])

  // Load column visibility settings from localStorage
  useEffect(() => {
    try {
      const storedColumns = localStorage.getItem(
        EXPEDIENT_TABLE_COLUMNS_STORAGE_KEY
      )
      if (storedColumns) {
        const parsedColumns = JSON.parse(storedColumns)
        // Validate that the parsed data is an object
        if (parsedColumns && typeof parsedColumns === 'object') {
          setColumnVisibility(parsedColumns)
        }
      }
    } catch (_error) {
      // DEBUG: Failed to load column visibility settings (_error available)
      void _error
      // Continue with default column visibility on error
    }
  }, [])

  const delCompany = useMutation({
    mutationFn: deleteCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_ALL_COMPANIES_QUERY_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: ['providers'],
      })
      queryClient.invalidateQueries({
        queryKey: ['managers'],
      })
      setIsDeleting(false)
      onCloseDelete()
      toast.success('La compañía ha sido eliminada con éxito.')
    },
    onError: (response: { response: { statusText?: string } }) => {
      setIsDeleting(false)
      onCloseDelete()
      toast.error(
        JSON.stringify(
          response.response.statusText ||
            'Lo siento, ha ocurrido un error intentando eliminar'
        )
      )
    },
  })

  // Handle column visibility changes - both update state and save to localStorage
  const handleColumnVisibilityChange = (
    updatedVisibility:
      | VisibilityState
      | ((_prev: VisibilityState) => VisibilityState)
  ) => {
    // First update the state
    setColumnVisibility(updatedVisibility)

    // Then save to localStorage
    try {
      // If it's a function, we need to get the actual value
      const newVisibility =
        typeof updatedVisibility === 'function'
          ? updatedVisibility(columnVisibility)
          : updatedVisibility

      if (Object.keys(newVisibility).length > 0) {
        localStorage.setItem(
          EXPEDIENT_TABLE_COLUMNS_STORAGE_KEY,
          JSON.stringify(newVisibility)
        )
      }
    } catch (_error) {
      // DEBUG: Failed to save column visibility settings (_error available)
      void _error
      // Continue without saving settings on error
    }
  }

  // Reset table when the page changes by recreating the table instance
  const table = useReactTable({
    data: tableData,
    columns: isLoading
      ? (skeletonColumns as ColumnDef<TData, unknown>[])
      : columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    state: {
      columnVisibility,
    },
    // This key ensures the table is reset when data changes
    manualPagination: true,
  })

  // Card view for mobile rendering
  const renderMobileCard = (row: Row<TData>) => {
    // For skeleton rows
    if (isLoading) {
      return (
        <div
          aria-rowindex={row.index + 2}
          className="mb-4 rounded-md border bg-card p-4 shadow-sm"
          key={row.id}
          role="row"
        >
          <div className="grid grid-cols-2 gap-2">
            {table.getVisibleLeafColumns().map((column) => (
              <div className="mb-2" key={column.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </div>
                <div className="flex h-5 items-center">
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div
        aria-rowindex={row.index + 2} // +2 for header row and 0-indexing
        className="mb-4 cursor-pointer rounded-md border bg-card p-4 shadow-sm hover:bg-accent/50"
        key={row.id}
        //onClick={(e) => navigateToExpedient(row, e)}
        role="row"
      >
        <div className="grid grid-cols-2 gap-2">
          {row
            .getVisibleCells()
            .map(
              (cell: {
                id: string
                column: { id: string; columnDef: { header?: string } }
                getValue: () => unknown
              }) => {
                // Skip rendering empty cells
                if (cell.column.id === 'shipping' && !row.original.shipping)
                  return null

                const header =
                  typeof cell.column.columnDef.header === 'string'
                    ? cell.column.columnDef.header
                    : cell.column.id

                return (
                  <div className="mb-2" key={cell.id} role="cell">
                    <div className="text-xs font-medium text-muted-foreground">
                      {header}
                    </div>
                    <div>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  </div>
                )
              }
            )}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      <HeaderActions
        columns={table.getAllColumns() as unknown as Column<Company, unknown>[]}
      />

      <div className="border-t">
        <div>
          {isLoading ||
          isFetching ||
          isError ||
          table.getRowModel().rows?.length ? (
            isMobileView ? (
              // Mobile card view
              <div aria-label="Tabla de facturas" className="p-4" role="table">
                <div className="sr-only" role="rowgroup">
                  <div role="row">
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <div key={header.id} role="columnheader">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div key={`page-${currentPage}`} role="rowgroup">
                  {table.getRowModel().rows.map((row) => renderMobileCard(row))}
                </div>
              </div>
            ) : (
              // Standard table view
              <div
                aria-label="Tabla de facturas"
                className="overflow-x-auto"
                role="region"
              >
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead
                              className="px-4 text-left"
                              key={header.id}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody key={`page-${currentPage}`}>
                    {table.getRowModel().rows.map((row) => {
                      // For skeleton rows during loading
                      if (isLoading) {
                        return (
                          <TableRow className="h-11 text-left" key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <TableCell
                                  className="px-0.5 py-1 text-left"
                                  key={cell.id}
                                >
                                  <Skeleton className="h-10 w-full rounded-none bg-gray-100" />
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        )
                      }

                      return (
                        <TableRow
                          className="hover:bg-accent/50"
                          data-state={row.getIsSelected() && 'selected'}
                          //onClick={(e) => navigateToExpedient(row, e)}
                          //className="cursor-pointer hover:bg-accent/50"
                          key={row.id}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              className="px-4 py-1 text-left"
                              key={cell.id}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {isError ? 'Error al cargar los datos' : 'No hay resultados'}
              </p>
            </div>
          )}
          <Navigation />
        </div>
      </div>
      <Modal
        isOpen={showEditCompany}
        onClose={onClose}
        showDefaultDescription={false}
        size={'xs'}
      >
        <NewCompanyModal close={onClose} companyToEdit={companyToEdit} />
      </Modal>
      <AlertDialog open={showDeleteCompany}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la compañía?</AlertDialogTitle>
            <AlertDialogDescription>
              Si elimina ahora, perderá todos los datos asociados a esta
              compañía. ¿Está seguro de que desea eliminar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onCloseDelete}>
              Cancelar
            </AlertDialogCancel>
            <Button
              action={() => {
                setIsDeleting(true)
                delCompany.mutate(companyToDelete as number)
              }}
              disabled={isDeleting}
              loading={isDeleting}
              name={'Sí, eliminar'}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
